// src/features/manual/components/ManualHelpButton.tsx
//
// «Ver en el manual»: the quiet link a screen's header shows, opening the manual at the section that
// explains that screen. Which section is the manual's own decision — each section claims its screens
// with a `Screens:` line (roommatik.analytics `ManualScreens.Known`) — so a screen no section of the
// reader's edition claims shows nothing. Page headers draw it from SectionHeader's `manualScreen`.

import React from 'react';
import { useLocaleState, useTranslate } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Tooltip } from '@mui/material';
import { MenuBookRounded as ManualIcon } from '@mui/icons-material';
import { getCurrentRole } from '@/utils/auth';
import { getImpersonation } from '@/utils/impersonation';
import { useManualScreenLink } from '../hooks/useManual';
import type { ManualScreenLink } from '../types';
import { manualLangFromLocale, manualSectionTarget } from '../utils/labels';

export interface ManualHelpButtonProps {
  /** The screen key, one of roommatik.analytics' `ManualScreens.Known`. */
  screen: string;
}

/** The route and router state that open the manual at a section (see ManualPage). */
export const manualHelpTarget = (link: ManualScreenLink): { to: string; state: { anchor: string | null } } =>
  manualSectionTarget(link.chapterId, link.anchor);

/**
 * The link for a resolved section: a text button in the link colour with a book glyph (the «?»
 * beside it is the screen's quick help), label underlined on hover. On a phone only the glyph stays,
 * in a 40 px round target.
 */
export const ManualHelpLink: React.FC<{ link: ManualScreenLink }> = ({ link }) => {
  const translate = useTranslate();
  const { to, state } = manualHelpTarget(link);
  const hint = translate('manual.help.hint');

  return (
    <Tooltip title={hint}>
      <Button
        component={RouterLink}
        to={to}
        state={state}
        variant="text"
        size="small"
        startIcon={<ManualIcon />}
        aria-label={hint}
        data-manual-help={link.screen}
        sx={(theme) => ({
          flexShrink: 0,
          minHeight: 32,
          fontSize: '0.75rem',
          fontWeight: 600,
          textTransform: 'none',
          color: theme.custom.text.brand,
          '& .MuiButton-startIcon': { mr: '4px', '& > *:nth-of-type(1)': { fontSize: '17px' } },
          '&:hover': {
            backgroundColor: 'transparent',
            '& .manual-help-label': { textDecoration: 'underline', textUnderlineOffset: '3px' },
          },
          '&:focus-visible': { boxShadow: theme.custom.focusRing },
          // Below the desktop width only the glyph stays: a 40 px round target on a phone,
          // the 32 px toolbar size in between.
          [theme.breakpoints.down('md')]: {
            minWidth: 32,
            width: 32,
            height: 32,
            px: 0,
            borderRadius: '50%',
            '& .MuiButton-startIcon': { mx: 0 },
          },
          [theme.breakpoints.down('sm')]: { minWidth: 40, width: 40, height: 40 },
        })}
      >
        <Box component="span" className="manual-help-label" sx={{ display: { xs: 'none', md: 'inline' } }}>
          {translate('manual.help.button')}
        </Box>
      </Button>
    </Tooltip>
  );
};

/**
 * Renders nothing while the index loads, when no section of the reader's edition claims the screen,
 * or when the index cannot be read: help must never get in the way of the screen it sits on.
 */
export const ManualHelpButton: React.FC<ManualHelpButtonProps> = ({ screen }) => {
  const [locale] = useLocaleState();
  const identity = `${getCurrentRole() ?? ''}:${getImpersonation()?.sub ?? ''}`;
  const link = useManualScreenLink(screen, manualLangFromLocale(locale), identity);
  return link ? <ManualHelpLink link={link} /> : null;
};

export default ManualHelpButton;
